/**
 * Rehab status widget. Mirrors the design: a 3-cell summary (in progress /
 * completed / budget deployed) above a progress list of the active rehabs.
 */
import { Check, Clock, DollarSign, Info } from "lucide-react";

import { DashboardCard } from "./DashboardCard";
import { fmtMoneyShort } from "./dashboard-metrics";

export interface ActiveRehab {
  id: string;
  name: string;
  /** Progress percent, 0–100. */
  pct: number;
}

interface RehabStatusCardProps {
  inProgress: number;
  completed: number;
  budgetDeployed: number;
  active: ActiveRehab[];
}

export function RehabStatusCard({
  inProgress,
  completed,
  budgetDeployed,
  active,
}: RehabStatusCardProps) {
  const summary: Array<{
    value: string;
    label: string;
    icon: typeof Clock;
    accent: string;
  }> = [
    {
      value: String(inProgress),
      label: "In progress",
      icon: Clock,
      accent: "bg-warning/15 text-warning",
    },
    {
      value: String(completed),
      label: "Completed",
      icon: Check,
      accent: "bg-success/15 text-success",
    },
    {
      value: fmtMoneyShort(budgetDeployed),
      label: "Budget deployed",
      icon: DollarSign,
      accent: "bg-primary/15 text-primary",
    },
  ];

  const emptyNote = completed
    ? `No active renovations right now. All ${completed} completed ${
        completed === 1 ? "rehab is" : "rehabs are"
      } reflected in current ARVs.`
    : "No active renovations right now.";

  return (
    <DashboardCard
      title="Rehab status"
      subtitle="Renovation progress across the portfolio"
      link={{ label: "Open rehab", to: "/rehab" }}
    >
      <div className="mb-4 grid grid-cols-3 gap-2.5">
        {summary.map((cell) => (
          <div
            key={cell.label}
            className="flex items-start justify-between gap-2.5 rounded-[9px] border border-border bg-muted px-3.5 py-3"
          >
            <div>
              <div className="text-[20px] font-extrabold leading-[1.1] text-foreground">
                {cell.value}
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">
                {cell.label}
              </div>
            </div>
            <div
              className={`grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg ${cell.accent}`}
            >
              <cell.icon className="h-3.5 w-3.5" aria-hidden />
            </div>
          </div>
        ))}
      </div>

      {active.length === 0 ? (
        <div className="flex items-center gap-2.5 rounded-[10px] border border-dashed border-border px-[18px] py-4 text-[13px] text-muted-foreground">
          <Info className="h-4 w-4 flex-shrink-0 text-muted-foreground/70" aria-hidden />
          <span>{emptyNote}</span>
        </div>
      ) : (
        <div className="flex flex-col gap-3.5">
          {active.map((r) => (
            <div key={r.id} className="flex flex-col gap-[7px]">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[13px] font-semibold text-foreground">
                  {r.name}
                </span>
                <span className="text-xs font-bold tabular-nums text-warning">
                  {r.pct}%
                </span>
              </div>
              <div className="h-[7px] overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-warning"
                  style={{ width: `${r.pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardCard>
  );
}
